import baseApi from './RootBaseApi';
import { ApiResponse, PaginatedApiResponse } from '@/types/api.types';
import {
  GuestBookingPlatformSetting,
  UpdateGuestBookingPlatformSettingPayload,
  HarborGuestBookingOverride,
  GuestBookingHarborOverrideQueryParams,
  UpdateHarborGuestBookingOverridePayload,
} from '@/types/GuestBookingGovernance.type';

export const GuestBookingGovernanceService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getGuestBookingGlobalSetting: builder.query<
      ApiResponse<GuestBookingPlatformSetting>,
      void
    >({
      query: () => ({
        url: '/guest-booking-governance/global',
        method: 'GET',
      }),
      providesTags: ['GuestBookingGovernance'],
    }),
    updateGuestBookingGlobalSetting: builder.mutation<
      ApiResponse<GuestBookingPlatformSetting>,
      UpdateGuestBookingPlatformSettingPayload
    >({
      query: (payload) => ({
        url: '/guest-booking-governance/global',
        method: 'PUT',
        data: payload,
      }),
      invalidatesTags: ['GuestBookingGovernance', 'ActivityLogs'],
    }),
    getGuestBookingHarborOverrides: builder.query<
      PaginatedApiResponse<HarborGuestBookingOverride[]>,
      GuestBookingHarborOverrideQueryParams
    >({
      query: (params) => ({
        url: '/guest-booking-governance/harbors',
        method: 'GET',
        params,
      }),
      // The backend wraps every response in { status, message, data }; for this
      // endpoint `data` itself is the paginated envelope (data/currentPage/pageLimit/
      // totalItems/totalPages), so unwrap one level to match PaginatedApiResponse<T>.
      transformResponse: (
        response: ApiResponse<PaginatedApiResponse<HarborGuestBookingOverride[]>>,
      ) => response.data,
      providesTags: ['GuestBookingGovernance'],
    }),
    getGuestBookingHarborOverride: builder.query<
      ApiResponse<HarborGuestBookingOverride>,
      string
    >({
      query: (harborId) => ({
        url: `/guest-booking-governance/harbors/${harborId}`,
        method: 'GET',
      }),
      providesTags: ['GuestBookingGovernance'],
    }),
    updateGuestBookingHarborOverride: builder.mutation<
      ApiResponse<HarborGuestBookingOverride>,
      { harborId: string; payload: UpdateHarborGuestBookingOverridePayload }
    >({
      query: ({ harborId, payload }) => ({
        url: `/guest-booking-governance/harbors/${harborId}`,
        method: 'PUT',
        data: payload,
      }),
      invalidatesTags: ['GuestBookingGovernance', 'ActivityLogs'],
    }),
  }),
});

export const {
  useGetGuestBookingGlobalSettingQuery,
  useUpdateGuestBookingGlobalSettingMutation,
  useGetGuestBookingHarborOverridesQuery,
  useGetGuestBookingHarborOverrideQuery,
  useUpdateGuestBookingHarborOverrideMutation,
} = GuestBookingGovernanceService;
